import { useQuery } from '@tanstack/react-query';
import { config } from '~/config';
import { useRequest } from '~/lib/useRequest';

export type HistoryRecord = {
  id: string;
  type: 'history';
  attributes: {
    referenceId: string;
    entityType: string;
    profileId: string;
    accountType?: string | null;
    changes: Record<string, { old: string; new: string }>;
  };
  meta: {
    createdAt: string;
    updatedAt: string;
  };
};

type GetEntityHistoriesResponse = {
  data: HistoryRecord[];
};

export type EntityHistoryEntry = {
  updatedAt: string;
  updatedByRole?: string;
};

export type EntityHistoryMap = Record<string, EntityHistoryEntry>;

/** Maps audit log accountType to display name shown in "Updated on ... by {name}". */
const ACCOUNT_TYPE_DISPLAY_NAMES: Record<string, string> = {
  school: 'Institution',
  agent: 'Agent',
  student: 'Student',
};

/** Exported for unit tests. Maps audit log accountType to display name. */
export function getAccountTypeDisplayName(accountType: string | undefined): string | undefined {
  if (!accountType) return undefined;
  return ACCOUNT_TYPE_DISPLAY_NAMES[accountType.toLowerCase()] ?? accountType;
}

type EntityHistoryResult = {
  map: EntityHistoryMap;
  records: HistoryRecord[];
};

export function useEntityHistory(entityType: string, entityId: string) {
  const request = useRequest();

  return useQuery({
    queryKey: ['entity-history', entityType, entityId],
    queryFn: async (): Promise<EntityHistoryResult> => {
      const url = `${config.apiHost}/private/v1/entity-histories/${entityType}/${entityId}`;
      const response = await request<GetEntityHistoriesResponse>(url, {
        method: 'GET',
        isExpectedResponse: (res): res is GetEntityHistoriesResponse =>
          Boolean(res && typeof res === 'object' && 'data' in res),
      });

      // Transform to a map: { referenceId: { updatedAt, updatedByRole } } for the most recent record per referenceId
      const historyMap: EntityHistoryMap = {};
      response.data.forEach((record) => {
        const refId = record.attributes.referenceId;
        const existing = historyMap[refId];
        const updatedAt = record.meta.updatedAt;
        if (!existing || new Date(updatedAt) > new Date(existing.updatedAt)) {
          historyMap[refId] = {
            updatedAt,
            updatedByRole: getAccountTypeDisplayName(record.attributes.accountType ?? undefined),
          };
        }
      });

      return { map: historyMap, records: response.data };
    },
    enabled: Boolean(entityId),
  });
}
